import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { products } from '../../data/products';
import ProductImageGallery from '../../components/ProductImageGallery';
import ProductCard from '../../components/ProductCard';

const LavaLamp = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [currentImage, setCurrentImage] = useState('');
  const [selectedColor, setSelectedColor] = useState('Orange');
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // 获取熔岩灯产品数据
  const lavaLamp = products.find(p => p.name === 'Lava Lamp – EU Plug Retro Wax Light for Home & Holiday Decoration');
  
  // 更多图片
  const additionalImages = [
    lavaLamp.image,
    "/images/shop-products/lava-lamp/1b7d1a2ce01d8e066743c9207a532a41.jpeg",
    "/images/shop-products/lava-lamp/3632433bbf87b6c113a4257df82c72b5.jpeg",
    "/images/shop-products/lava-lamp/37e2f8fecaca97b155ee8336548a1ae8.jpeg",
    "/images/shop-products/lava-lamp/3a6d8f11370187042ff985eb1a6db17a.jpeg",
    "/images/shop-products/lava-lamp/699ed142bc840bf5dfcbdb57b4d851b8.jpeg",
    "/images/shop-products/lava-lamp/76cd3c26d44539a01e6dcd5c924e86c1.jpeg",
    "/images/shop-products/lava-lamp/b91b8f6078ca410bd53709f7141c891e.jpeg",
    "/images/shop-products/lava-lamp/e711f5bf25c664053bf7085db802c8f1.jpeg"
  ];

  // 初始化当前图片
  useEffect(() => {
    setCurrentImage(lavaLamp.image);
  }, [lavaLamp.image]);

  // 计算折扣百分比
  const getDiscountPercentage = () => {
    const currentPrice = parseFloat(lavaLamp.price.replace('S$', ''));
    const currentOriginalPrice = parseFloat(lavaLamp.originalPrice.replace('S$', ''));
    const discount = ((currentOriginalPrice - currentPrice) / currentOriginalPrice) * 100;
    return Math.round(discount);
  };

  // 处理添加到购物车
  const handleAddToCart = async () => {
    setIsAddingToCart(true);
    
    const productToAdd = {
      ...lavaLamp,
      selectedColor,
      quantity
    };
    
    addToCart(productToAdd);
    
    // 模拟加载延迟
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsAddingToCart(false);
  };

  // 处理立即购买
  const handleBuyNow = async () => {
    await handleAddToCart();
    navigate('/cart');
  };

  // 获取相关产品
  const getRelatedProducts = () => {
    const relatedProducts = products.filter(p => 
      p.category === 'home-decor' || 
      p.category === 'lighting' || 
      p.category === 'gaming-accessories'
    ).filter(p => p.name !== 'Lava Lamp – EU Plug Retro Wax Light for Home & Holiday Decoration');
    
    const otherProducts = products.filter(p => 
      p.name !== 'Lava Lamp – EU Plug Retro Wax Light for Home & Holiday Decoration' && 
      !relatedProducts.some(rp => rp.id === p.id)
    );
    
    return [...relatedProducts, ...otherProducts].slice(0, 4);
  };
  
  const displayProducts = getRelatedProducts();

  if (!lavaLamp) {
    return <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="text-white text-xl">Product not found</div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 左侧图片区域 */}
          <div className="space-y-4">
            <ProductImageGallery 
              images={additionalImages}
              currentImage={currentImage}
              onImageChange={setCurrentImage}
            />
          </div>

          {/* 右侧产品信息 */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">{lavaLamp.name}</h1>
              
              {/* 评分 */}
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className={i < Math.floor(lavaLamp.rating) ? "text-yellow-400" : "text-gray-600"}>★</span>
                  ))}
                </div>
                <span className="ml-2 text-gray-400">({lavaLamp.reviews} reviews)</span>
              </div>
              
              {/* 价格 */}
              <div className="mb-6">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="text-3xl font-bold text-white">{lavaLamp.price}</span>
                  <span className="text-xl text-gray-400 line-through">{lavaLamp.originalPrice}</span>
                  <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">
                    -{getDiscountPercentage()}%
                  </span>
                </div>
              </div>
              
              {/* 库存状态 */}
              <div className="mb-6">
                {lavaLamp.inStock ? (
                  <span className="text-green-500 flex items-center">
                    <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    In Stock
                  </span>
                ) : (
                  <span className="text-red-500 flex items-center">
                    <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    Out of Stock
                  </span>
                )}
              </div>

              {/* 产品描述 */}
              <div className="mb-6">
                <p className="text-gray-300 leading-relaxed">{lavaLamp.description}</p>
              </div>

              {/* 选择颜色 */}
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3">Choose Colour</h3>
                <div className="flex gap-3">
                  {[
                    {
                      name: 'Red',
                      description: 'Clear Red Wax',
                      image: '/images/shop-products/lava-lamp/red.png',
                      soldOut: true
                    },
                    {
                      name: 'Blue',
                      description: 'Clear Blue Wax',
                      image: '/images/shop-products/lava-lamp/blue.png',
                      soldOut: true
                    },
                    {
                      name: 'Green',
                      description: 'Clear Green Wax',
                      image: '/images/shop-products/lava-lamp/green.png',
                      soldOut: true
                    },
                    {
                      name: 'Orange',
                      description: 'Clear Orange Wax',
                      image: '/images/shop-products/lava-lamp/orange.png',
                      soldOut: false
                    }
                  ].map((colorOption) => (
                    <div key={colorOption.name} className="flex flex-col items-center">
                      <div className="relative">
                        <button 
                          className={`border-2 ${colorOption.name === selectedColor ? 'border-green-500' : 'border-gray-700'} 
                            rounded-md overflow-hidden w-20 h-20 hover:border-green-500 transition-colors ${colorOption.soldOut ? 'opacity-50' : ''}`}
                          onClick={() => !colorOption.soldOut && setSelectedColor(colorOption.name)}
                          disabled={colorOption.soldOut}
                        >
                          <img 
                            src={colorOption.image} 
                            alt={colorOption.name}
                            className="w-full h-full object-cover"
                          />
                        </button>
                        {colorOption.soldOut && (
                          <div className="absolute inset-0 bg-red-500 bg-opacity-90 flex items-center justify-center rounded-md">
                            <span className="text-white text-xs font-bold">SOLD OUT</span>
                          </div>
                        )}
                      </div>
                      <div className="text-center mt-2">
                        <div className="text-sm text-white font-medium">{colorOption.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Quantity */}
              <div className="mb-8">
                <h3 className="text-lg font-medium mb-3">Quantity</h3>
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-full bg-gray-700 hover:bg-gray-600 flex items-center justify-center"
                  >
                    -
                  </button>
                  <span className="text-xl font-medium w-12 text-center">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 rounded-full bg-gray-700 hover:bg-gray-600 flex items-center justify-center"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex space-x-4 mb-6">
                <button
                  onClick={handleAddToCart}
                  disabled={!lavaLamp.inStock || isAddingToCart}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition-colors"
                >
                  {isAddingToCart ? 'Adding...' : 'Add to Cart'}
                </button>
                <button
                  onClick={handleBuyNow}
                  disabled={!lavaLamp.inStock || isAddingToCart}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition-colors"
                >
                  {isAddingToCart ? 'Processing...' : 'Buy Now'}
                </button>
              </div>

              {/* 配送信息 */}
              <div className="border-t border-gray-700 pt-6">
                <div className="flex items-center mb-3">
                  <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2a1 1 0 00.9-.5l1.5-2A1 1 0 0015 7h-3V4a1 1 0 00-1-1H3zM14 7l-1.5 2H10V5h4v2z" />
                  </svg>
                  <span className="text-gray-300">Free Meet Up (Available at Sengkang & Orchard locations)</span>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">Available Shipping</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 产品详情标签页 */}
        <div className="mt-16">
          {/* 标签页导航 */}
          <div className="border-b border-gray-700 mb-8">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`pb-4 text-lg font-medium ${
                  activeTab === 'overview' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('specifications')}
                className={`pb-4 text-lg font-medium ${
                  activeTab === 'specifications' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Specifications
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`pb-4 text-lg font-medium ${
                  activeTab === 'reviews' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Reviews
              </button>
            </nav>
          </div>

          {/* 标签页内容 */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* 产品概述 */}
              <div>
                <h2 className="text-2xl font-bold mb-6 text-white">Product Overview</h2>
                <div className="space-y-4 text-gray-300">
                  <p>
                    Experience the mesmerizing beauty of our premium lava lamp, featuring crystal-clear liquid motion and ambient lighting that creates the perfect atmosphere for relaxation and entertainment.
                  </p>
                  <p>
                    Designed for modern homes, offices, and gaming setups, this lava lamp delivers smooth, hypnotic wax movement with zero noise, making it ideal for creating a calming environment while you work, play, or unwind.
                  </p>
                  <p>
                    Built with cutting-edge technology and robust construction, this lava lamp is reliable and future-proof for all your ambient lighting and decorative needs.
                  </p>
                </div>
              </div>

              {/* 主要特色 */}
              <div>
                <h2 className="text-2xl font-bold mb-6 text-white">Key Features</h2>
                <div className="space-y-4">
                  {[
                    {
                      title: "Mesmerizing Motion",
                      description: "Smooth, hypnotic wax movement creates a captivating visual experience."
                    },
                    {
                      title: "Premium Construction",
                      description: "High-quality materials ensure durability and long-lasting performance."
                    },
                    {
                      title: "Ambient Lighting",
                      description: "Perfect warm glow creates a relaxing atmosphere for any room."
                    },
                    {
                      title: "Silent Operation",
                      description: "Whisper-quiet operation won't disturb your focus or sleep."
                    },
                    {
                      title: "Easy Maintenance",
                      description: "Simple setup and maintenance for hassle-free enjoyment."
                    }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{feature.title}</h3>
                        <p className="text-gray-300 text-sm">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'specifications' && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-6 text-white">Specifications</h2>
              <table className="w-full">
                <tbody>
                  {Object.entries(lavaLamp.specs).map(([key, value]) => (
                    <tr key={key} className="border-b border-gray-700">
                      <td className="py-3 text-gray-400 font-medium">{key}</td>
                      <td className="py-3 text-white">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-6 text-white">Customer Reviews</h2>
              <div className="space-y-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400 mr-4">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-400">★</span>
                    ))}
                  </div>
                  <span className="text-gray-300">({lavaLamp.reviews} reviews)</span>
                </div>
                <div className="text-gray-300">
                  <p>Reviews and ratings will be displayed here.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 相关产品 */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold mb-8">You May Also Like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayProducts.map((product) => (
              <div key={product.id} onClick={() => navigate(`/product/${product.id}`)} className="cursor-pointer">
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LavaLamp;
